import {
  Button,
  ButtonsGroup,
  Flex,
  Modal,
  Radio,
  RadioGroup,
  Spacing,
  Text,
  Tooltip,
  useModal,
} from '@billy/design-system'
import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { useSegment } from '../../../../hooks/useSegment'
import { ScoutReceiptDeletionReason } from '../../enums/scoutReceiptDeletionReason'

interface ScoutReceiptDeletionReasonType {
  value: ScoutReceiptDeletionReason
  labelKey: string
}

const scoutReceiptDeletionReasons: ScoutReceiptDeletionReasonType[] = [
  {
    value: ScoutReceiptDeletionReason.Duplicate,
    labelKey: 'receipts.delete_modal.help_us.reason.duplicate',
  },
  {
    value: ScoutReceiptDeletionReason.NotReceipt,
    labelKey: 'receipts.delete_modal.help_us.reason.not_receipt',
  },
  {
    value: ScoutReceiptDeletionReason.Other,
    labelKey: 'receipts.delete_modal.help_us.reason.other',
  },
]

interface ScoutReceiptDeletionConfirmationModalProps {
  isDeleting: boolean
  onDeleteClick: (reason?: ScoutReceiptDeletionReason) => void
  selectedReceiptsCount: number
}

export const ScoutReceiptDeletionConfirmationModal = ({
  isDeleting,
  onDeleteClick,
  selectedReceiptsCount,
}: ScoutReceiptDeletionConfirmationModalProps) => {
  const [selectedReason, setSelectedReason] = useState<ScoutReceiptDeletionReason>()
  const { close: closeModal } = useModal(ModalId.DeleteReceiptsConfirmationModal)
  const { t } = useTranslation()
  const { track } = useSegment()

  const hideTooltip = !!selectedReason || isDeleting

  const handleReasonClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedReason(event.target.value as ScoutReceiptDeletionReason)
    },
    [setSelectedReason],
  )
  const handleCancelClick = useCallback(() => {
    closeModal()
    setSelectedReason(undefined)
  }, [closeModal, setSelectedReason])

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(selectedReason)
    setSelectedReason(undefined)
    track('xxx - scout - deletion reason selected', {
      reason: selectedReason,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      deleted_count: selectedReceiptsCount,
    })
  }, [onDeleteClick, selectedReason, setSelectedReason, track, selectedReceiptsCount])

  return (
    <Modal id={ModalId.DeleteReceiptsConfirmationModal} size="s">
      <Modal.Header title={t('receipts.delete_modal.help_us.title', { count: selectedReceiptsCount })} />
      <Modal.Body>
        <Flex flexDirection="column" gap={Spacing.S16}>
          <Text colorVariant="primary" variant="bodySM">
            {t('receipts.delete_modal.help_us.message', { count: selectedReceiptsCount })}
          </Text>
          <RadioGroup direction="column" onChange={handleReasonClick} value={selectedReason}>
            {scoutReceiptDeletionReasons.map((reason: ScoutReceiptDeletionReasonType) => (
              <Radio key={reason.value} value={reason.value}>
                {t(reason.labelKey, { count: selectedReceiptsCount })}
              </Radio>
            ))}
          </RadioGroup>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={handleCancelClick} variant="text">
            {t('cancel')}
          </Button>
          <Tooltip forceHidden={hideTooltip} label={t('receipts.delete_modal.ok.disabled.tooltip')}>
            <Button color="danger" disabled={!selectedReason} loading={isDeleting} onClick={handleDeleteClick}>
              {t('receipts.delete_modal.ok_label')}
            </Button>
          </Tooltip>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
